import { AnswerValue } from "@/commonTypes/answer";
import { Question, QuestionType, TextQuestion } from "@/commonTypes/question";

/**
 * Validates answer based on its type:
 * - string: TEXT, DATE_PICKER - must be non-empty
 * - string[]: MULTIPLE_CHOICE - must have at least one selection
 * - Record<string, string>: TEXT_GROUP - all field values must be non-empty
 * - TRANSITION questions always return true (no answer required)
 */
export function isAnswerValid(
  answer: AnswerValue | null | undefined,
  question?: Question,
): boolean {
  // TRANSITION and SUBMIT questions don't require an answer to proceed
  if (
    question?.questionType === QuestionType.TRANSITION ||
    question?.questionType === QuestionType.SUBMIT
  ) {
    return true;
  }

  // All-optional TEXT questions can be skipped entirely (answer may be null or all empty)
  if (
    question?.questionType === QuestionType.TEXT &&
    (question as TextQuestion).answers?.every((f) => f.optional)
  ) {
    if (!answer) return true;
    if (typeof answer === "object" && !Array.isArray(answer)) {
      const values = Object.values(answer);
      if (values.every((v) => v.length === 0)) return true;
    }
  }

  if (!answer) return false;

  // MULTIPLE_CHOICE: array must have selections
  if (Array.isArray(answer)) {
    return answer.length > 0;
  }

  // TEXT_GROUP / TEXT: all field values must be non-empty
  if (typeof answer === "object") {
    const values = Object.values(answer);
    return values.length > 0 && values.every((v) => v.length > 0);
  }

  // TEXT, DATE_PICKER: string must be non-empty
  return answer.length > 0;
}
